import { Controller } from "@hotwired/stimulus"
import { showToast } from "../helpers/toast"

export default class extends Controller {
  static targets = [
    "checkbox", "selectAll", "toggleAll", "count", "submitBtn", "owner",
    "actionBar", "toolbar", "enrichForm", "phoneForm", "enrichInputs", "phoneInputs",
    "stickyBar"
  ]
  static values = { paramName: { type: String, default: "contact_ids[]" } }

  connect() {
    this.updateState()
  }

  toggle() {
    this.updateState()
  }

  toggleAll() {
    const source = this.hasSelectAllTarget ? this.selectAllTarget : this.toggleAllTarget
    const checked = source.checked
    this.checkboxTargets.forEach(cb => cb.checked = checked)
    this.updateState()
  }

  updateCount() {
    this.updateState()
  }

  updateState() {
    const selected = this.checkboxTargets.filter(cb => cb.checked)
    const count = selected.length
    const total = this.checkboxTargets.length

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${count} sélectionné${count > 1 ? 's' : ''}`
    }

    const allTarget = this.hasSelectAllTarget ? this.selectAllTarget : (this.hasToggleAllTarget ? this.toggleAllTarget : null)
    if (allTarget) {
      allTarget.checked = count === total && total > 0
      allTarget.indeterminate = count > 0 && count < total
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = count === 0
    }

    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.toggle("hidden", count === 0)
    }

    if (this.hasToolbarTarget) {
      this.toolbarTarget.classList.toggle("hidden", count === 0)
      this.toolbarTarget.classList.toggle("flex", count > 0)
    }

    if (this.hasStickyBarTarget) {
      this.stickyBarTarget.dataset.bulkVisible = count > 0 ? "true" : "false"
    }

    const ids = selected.map(cb => cb.value)
    this.syncFormInputs(this.enrichInputsTargets, ids)
    this.syncFormInputs(this.phoneInputsTargets, ids)
  }

  // Story 1.7 final (21/05/2026) — Sélection bypass page : flag + tous checkboxes coches.
  selectAllPages() {
    this._allPagesSelected = true
    this.checkboxTargets.forEach(cb => { cb.checked = true })
    if (this.hasCountTarget) this.countTarget.textContent = "tous sélectionnés"
    if (this.hasActionBarTarget) this.actionBarTarget.classList.remove("hidden")
  }

  deleteSelected(event) {
    const btn = event.currentTarget
    const resource = btn.dataset.resource
    const message = btn.dataset.confirm || "Supprimer les éléments sélectionnés ?"
    const ids = this.checkboxTargets.filter(cb => cb.checked).map(cb => cb.value)
    if (ids.length === 0 && !this._allPagesSelected) return
    const targetCount = this._allPagesSelected ? "tous les contacts filtrés" : `${ids.length} contact(s)`
    if (!confirm(message.replace(/\?$/, "") + ` (${targetCount}) ?`)) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/${resource}/bulk_destroy`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ ids, all_pages: this._allPagesSelected || false })
    }).then(resp => {
      if (resp.ok) {
        this.checkboxTargets.filter(cb => cb.checked).forEach(cb => {
          cb.closest("tr")?.remove()
        })
        showToast(`${ids.length} élément${ids.length > 1 ? 's' : ''} supprimé${ids.length > 1 ? 's' : ''}`)
        this.updateState()
      } else {
        showToast("Erreur lors de la suppression.", "error")
      }
    })
  }

  // Story 1.7 (21/05/2026) — Bulk reassign owner B2C.
  // Le select déclencheur expose data-url="/contacts-b2c/bulk_reassign".
  reassignSelected(event) {
    const select = event.currentTarget
    const ownerId = select.value
    if (!ownerId) return
    const ids = this.checkboxTargets.filter(cb => cb.checked).map(cb => cb.value)
    if (ids.length === 0 && !this._allPagesSelected) { select.value = ""; return }
    const targetCount = this._allPagesSelected ? "tous les contacts filtrés" : `${ids.length} contact(s)`
    if (!confirm(`Réassigner ${targetCount} au commercial sélectionné ?`)) {
      select.value = ""
      return
    }
    this._bulkPatch(select.dataset.url, { ids, owner_id: ownerId, all_pages: this._allPagesSelected || false }, "réassigné", select)
  }

  // Story 1.7 (21/05/2026) — Bulk update lead status B2C.
  updateStatusSelected(event) {
    const select = event.currentTarget
    const status = select.value
    if (!status) return
    const ids = this.checkboxTargets.filter(cb => cb.checked).map(cb => cb.value)
    if (ids.length === 0 && !this._allPagesSelected) { select.value = ""; return }
    const targetCount = this._allPagesSelected ? "tous les contacts filtrés" : `${ids.length} contact(s)`
    if (!confirm(`Changer le statut de ${targetCount} ?`)) {
      select.value = ""
      return
    }
    this._bulkPatch(select.dataset.url, { ids, lead_status: status, all_pages: this._allPagesSelected || false }, "mis à jour", select)
  }

  _bulkPatch(url, body, verb, resetEl) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify(body)
    }).then(resp => {
      if (resp.ok) {
        const n = body.ids.length
        showToast(`${n} contact${n > 1 ? "s" : ""} ${verb}`)
        setTimeout(() => window.location.reload(), 400)
      } else {
        showToast("Erreur lors de l'opération.", "error")
        if (resetEl) resetEl.value = ""
      }
    })
  }

  syncFormInputs(containers, ids) {
    const paramName = this.paramNameValue
    containers.forEach(container => {
      container.innerHTML = ids.map(id =>
        `<input type="hidden" name="${paramName}" value="${id}">`
      ).join("")
    })
  }
}
